import {ActionList} from '@primer/react'
import {CheckIcon, SquareFillIcon, XCircleFillIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import type {ChatState, ToolCallState, ToolResultContent} from '@microsoft/agent-host-protocol'
import {useEffect, useMemo, useRef, useState, type FocusEvent, type ReactElement, type RefObject} from 'react'

import {ahpToolCallToToolProps} from '../../ahp/ahp-tool-helpers'
import {isSystemNotificationMessage, parseBackgroundShellNotification} from '../../utils/background-task-notifications'
import {useRegisterBackgroundActivity} from '../../hooks/use-background-activity-presence'
import {InProgressSpinner} from '../StatusIcon'
import {ActionBarTrigger} from './AhpTaskChatInputActionBar/ActionBarTrigger'
import {AhpTaskChatInputActionBar} from './AhpTaskChatInputActionBar/AhpTaskChatInputActionBar'
import {ItemsPanel} from './AhpTaskChatInputActionBar/ItemsPanel'
import styles from './BackgroundTasks.module.css'

export type BackgroundTaskStatus = 'running' | 'completed' | 'failed' | 'cancelled'

interface BackgroundTaskBase {
  id: string
  description: string
  status: BackgroundTaskStatus
  startedAt?: string
  completedAt?: string
}

export interface BackgroundShellTask extends BackgroundTaskBase {
  type: 'shell'
  command: string
}

export type BackgroundTask = BackgroundShellTask

const SHELL_COMPLETION_PATTERN = /<shellId: ([^\s>]+) completed with exit code (\d+)>/i
const BACKGROUND_SHELL_START_PREFIXES = [
  'command started in detached background with shellid:',
  'command started in background with shellid:',
]
const RUNNING_SHELL_PREFIX = 'command with shellid:'

function isShellIdWhitespace(character: string): boolean {
  return character === ' ' || character === '\t' || character === '\n' || character === '\r'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toolArguments(toolCall: ToolCallState): Record<string, unknown> {
  const args = ahpToolCallToToolProps(toolCall).args
  return isRecord(args) ? args : {}
}

function contentText(content: ToolResultContent[] | undefined): string {
  return (
    content
      ?.map(block => {
        if (block.type === 'text') return block.text
        if (block.type === 'terminal') return block.result?.preview ?? ''
        return ''
      })
      .join('') ?? ''
  )
}

function toolResultText(toolCall: ToolCallState): string {
  if (!('content' in toolCall)) return ''
  return contentText(toolCall.content)
}

function shellIdAfterPrefix(
  output: string,
  normalizedOutput: string,
  prefix: string,
): {shellId: string; valueEnd: number} | undefined {
  const prefixIndex = normalizedOutput.indexOf(prefix)
  if (prefixIndex === -1) return undefined

  let valueStart = prefixIndex + prefix.length
  while (isShellIdWhitespace(output[valueStart] ?? '')) valueStart += 1

  let valueEnd = valueStart
  while (
    valueEnd < output.length &&
    !isShellIdWhitespace(output[valueEnd] ?? '') &&
    output[valueEnd] !== '.' &&
    output[valueEnd] !== '>'
  ) {
    valueEnd += 1
  }

  const shellId = output.slice(valueStart, valueEnd)
  return shellId ? {shellId, valueEnd} : undefined
}

function backgroundShellId(toolCall: ToolCallState): string | undefined {
  const output = toolResultText(toolCall)
  const normalizedOutput = output.toLowerCase()

  for (const prefix of BACKGROUND_SHELL_START_PREFIXES) {
    const match = shellIdAfterPrefix(output, normalizedOutput, prefix)
    if (match) return match.shellId
  }

  const match = shellIdAfterPrefix(output, normalizedOutput, RUNNING_SHELL_PREFIX)
  if (!match) return undefined
  const suffix = normalizedOutput.slice(match.valueEnd)
  if (suffix.startsWith(' is running') || suffix.startsWith(' is still running')) return match.shellId

  return undefined
}

function updateRunningTask(
  tasks: Map<string, BackgroundTask>,
  shellId: string,
  status: Exclude<BackgroundTaskStatus, 'running'>,
  completedAt?: string,
) {
  const task = tasks.get(shellId)
  if (!task || task.status !== 'running') return
  tasks.set(shellId, {...task, status, completedAt})
}

function processToolCall(tasks: Map<string, BackgroundTask>, toolCall: ToolCallState, startedAt?: string) {
  if (toolCall.toolName === 'bash') {
    const shellId = backgroundShellId(toolCall)
    if (shellId) {
      const args = toolArguments(toolCall)
      const command = typeof args.command === 'string' ? args.command : ''
      const description = typeof args.description === 'string' ? args.description : command
      const existing = tasks.get(shellId)
      if (existing && existing.status !== 'running') return
      tasks.set(shellId, {
        type: 'shell',
        id: shellId,
        description: description || `Shell ${shellId}`,
        command,
        status: 'running',
        startedAt,
      })
    }
  }

  const args = toolArguments(toolCall)
  const shellId =
    typeof args.shellId === 'string' || typeof args.shellId === 'number' ? String(args.shellId) : undefined
  if (!shellId || toolCall.status !== 'completed') return

  if (toolCall.toolName === 'stop_bash' && toolCall.success) {
    updateRunningTask(tasks, shellId, 'cancelled', startedAt)
    return
  }

  // The host reports an exited shell as a terminal block with an exit code; the shell ID comes from the call's arguments.
  const terminalExitCode = toolCall.content?.flatMap(block =>
    block.type === 'terminal' && block.result ? [block.result.exitCode] : [],
  )[0]
  if (typeof terminalExitCode === 'number') {
    updateRunningTask(tasks, shellId, terminalExitCode === 0 ? 'completed' : 'failed', startedAt)
    return
  }

  const completion = toolResultText(toolCall).match(SHELL_COMPLETION_PATTERN)
  if (completion?.[1] === shellId) {
    updateRunningTask(tasks, shellId, completion[2] === '0' ? 'completed' : 'failed', startedAt)
  }
}

function processSystemNotification(tasks: Map<string, BackgroundTask>, text: string, completedAt?: string) {
  const completion = parseBackgroundShellNotification(text)
  if (!completion) return
  updateRunningTask(tasks, completion.shellId, completion.status, completedAt)
}

function processChatState(tasks: Map<string, BackgroundTask>, state: ChatState | undefined) {
  if (!state) return
  const turns = state.activeTurn ? [...state.turns, state.activeTurn] : state.turns

  for (const turn of turns) {
    if (isSystemNotificationMessage(turn.message)) {
      processSystemNotification(tasks, turn.message.text, turn.startedAt)
    }
    const responseParts = Array.isArray(turn.responseParts) ? turn.responseParts : []
    for (const part of responseParts) {
      if (part.kind === 'toolCall') processToolCall(tasks, part.toolCall, turn.startedAt)
      if (part.kind === 'systemNotification') {
        processSystemNotification(
          tasks,
          typeof part.content === 'string' ? part.content : part.content.markdown,
          turn.startedAt,
        )
      }
    }
  }
}

export function deriveBackgroundTasks(...states: Array<ChatState | undefined>): readonly BackgroundTask[] {
  const tasks = new Map<string, BackgroundTask>()
  for (const state of states) processChatState(tasks, state)
  return [...tasks.values()]
}

function statusPresentation(status: BackgroundTaskStatus) {
  switch (status) {
    case 'running':
      return {label: 'Running', icon: <InProgressSpinner />}
    case 'completed':
      return {label: 'Done', icon: <CheckIcon className={styles.completed} />}
    case 'failed':
      return {label: 'Failed', icon: <XCircleFillIcon className={styles.failed} />}
    case 'cancelled':
      return {label: 'Cancelled', icon: <SquareFillIcon className={styles.cancelled} />}
  }
}

function BackgroundTaskItem({task}: {task: BackgroundTask}) {
  const {label, icon} = statusPresentation(task.status)

  return (
    <ActionList.Item tabIndex={-1}>
      <ActionList.LeadingVisual>
        <span aria-hidden="true">{icon}</span>
      </ActionList.LeadingVisual>
      <span className={styles.description}>{task.description}</span>
      <ActionList.TrailingVisual>
        <span className={styles.status}>{label}</span>
      </ActionList.TrailingVisual>
      <ActionList.Description variant="block">
        <span className={styles.detail}>{task.command}</span>
      </ActionList.Description>
    </ActionList.Item>
  )
}

export interface BackgroundTasksProps {
  tasks: readonly BackgroundTask[]
  defaultOpen?: boolean
  focusTargetRef?: RefObject<HTMLTextAreaElement | null>
}

interface BackgroundTasksAction {
  panel: ReactElement | null
  trigger: ReactElement | null
}

export function useBackgroundTasksAction({
  tasks,
  defaultOpen = false,
  focusTargetRef,
}: BackgroundTasksProps): BackgroundTasksAction {
  const [open, setOpen] = useState(defaultOpen)
  const hasTasks = tasks.length > 0
  const openRef = useRef(defaultOpen)
  const focusWithinRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (open) {
      closeButtonRef.current?.focus()
      wasOpenRef.current = true
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
      wasOpenRef.current = false
    }
  }, [open])

  useEffect(
    () => () => {
      if (openRef.current && focusWithinRef.current && focusTargetRef?.current?.isConnected) {
        focusTargetRef.current.focus()
      }
    },
    [focusTargetRef],
  )

  const focusTrackingProps = {
    onFocusCapture: () => {
      focusWithinRef.current = true
    },
    onBlurCapture: (event: FocusEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget)) focusWithinRef.current = false
    },
  }

  const setPanelOpen = (nextOpen: boolean) => {
    openRef.current = nextOpen
    setOpen(nextOpen)
  }

  const panel =
    hasTasks && open ? (
      <div className={styles.ActionContents} {...focusTrackingProps}>
        <ItemsPanel
          title="Background activity"
          nonInteractive
          items={tasks.map(task => (
            <BackgroundTaskItem key={`${task.type}-${task.id}`} task={task} />
          ))}
          closeButtonRef={closeButtonRef}
          onClose={() => setPanelOpen(false)}
        />
      </div>
    ) : null

  const trigger = hasTasks ? (
    <span className={styles.ActionContents} {...focusTrackingProps}>
      <ActionBarTrigger ref={triggerRef} aria-expanded={open} count={tasks.length} onClick={() => setPanelOpen(!open)}>
        Background
      </ActionBarTrigger>
    </span>
  ) : null

  return {panel, trigger}
}

function VisibleBackgroundTasks(props: BackgroundTasksProps) {
  const action = useBackgroundTasksAction(props)

  return (
    <div className={styles.container}>
      {action.panel}
      <AhpTaskChatInputActionBar>{action.trigger}</AhpTaskChatInputActionBar>
    </div>
  )
}

export function BackgroundTasks(props: BackgroundTasksProps) {
  if (props.tasks.length === 0) return null
  return <VisibleBackgroundTasks {...props} />
}

export interface EstablishedBackgroundTasksProps extends Pick<BackgroundTasksProps, 'focusTargetRef'> {
  sessionId: string
  persistedState?: ChatState
}

export function usePersistedBackgroundTasks(sessionId: string, persistedState?: ChatState) {
  const tasks = useMemo(() => deriveBackgroundTasks(persistedState), [persistedState])
  const taskIds = useMemo(() => tasks.map(task => task.id), [tasks])
  useRegisterBackgroundActivity(sessionId, taskIds)
  return tasks
}

export function useEstablishedBackgroundTasks(sessionId: string, persistedState?: ChatState) {
  const {state} = useEstablishedAhpSessionViewState()
  const tasks = useMemo(() => deriveBackgroundTasks(persistedState, state), [persistedState, state])
  const taskIds = useMemo(() => tasks.map(task => task.id), [tasks])
  useRegisterBackgroundActivity(sessionId, taskIds)
  const previousStatusesRef = useRef<Map<string, BackgroundTaskStatus> | undefined>(undefined)

  useEffect(() => {
    const previous = previousStatusesRef.current
    if (previous) {
      for (const task of tasks) {
        if (task.status !== 'running' && previous.get(task.id) === 'running') {
          announce(`Background activity ${task.description} ${task.status}`)
        }
      }
    }
    previousStatusesRef.current = new Map(tasks.map(task => [task.id, task.status]))
  }, [tasks])

  return tasks
}

export function EstablishedBackgroundTasks({
  focusTargetRef,
  sessionId,
  persistedState,
}: EstablishedBackgroundTasksProps) {
  const tasks = useEstablishedBackgroundTasks(sessionId, persistedState)
  return <BackgroundTasks tasks={tasks} focusTargetRef={focusTargetRef} />
}
